// ─────────────────────────────────────────────────────────────
// SuperAdmin — Catalogue des permissions « plateforme ».
// Permet au superadmin principal d'ajouter des travailleurs qui gèrent
// la plateforme avec des autorisations précises. Les pages du superadmin
// sont masquées/bloquées selon ces permissions.
// ─────────────────────────────────────────────────────────────

export interface PlatformPermissionDef {
  key: string;
  label: string;
}

export interface PlatformPermissionGroup {
  key: string;
  label: string;
  icon: string;
  permissions: PlatformPermissionDef[];
}

export const PLATFORM_PERMISSION_GROUPS: PlatformPermissionGroup[] = [
  {
    key: 'users',
    label: 'Utilisateurs',
    icon: 'ri-user-line',
    permissions: [
      { key: 'users.read', label: 'Consulter les utilisateurs' },
      { key: 'users.write', label: 'Gérer les utilisateurs (activer / désactiver)' },
    ],
  },
  {
    key: 'team',
    label: 'Équipe plateforme',
    icon: 'ri-admin-line',
    permissions: [
      { key: 'team.read', label: "Consulter l'équipe plateforme" },
      { key: 'team.manage', label: 'Ajouter / gérer les travailleurs de la plateforme' },
    ],
  },
  {
    key: 'activity',
    label: "Journal d'activité",
    icon: 'ri-history-line',
    permissions: [
      { key: 'activity.read', label: "Consulter le journal d'activité du back-office" },
    ],
  },
  {
    key: 'themes',
    label: 'Thèmes & Design',
    icon: 'ri-palette-line',
    permissions: [
      { key: 'themes.read', label: 'Consulter les thèmes' },
      { key: 'themes.write', label: 'Créer et modifier les thèmes' },
    ],
  },
  {
    key: 'apps',
    label: 'App Builder',
    icon: 'ri-apps-2-line',
    permissions: [
      { key: 'apps.read', label: 'Consulter les applications' },
      { key: 'apps.write', label: 'Créer et modifier les applications' },
    ],
  },
  {
    key: 'formations',
    label: 'Formations',
    icon: 'ri-graduation-cap-line',
    permissions: [
      { key: 'formations.read', label: 'Consulter les formations' },
      { key: 'formations.write', label: 'Gérer les formations' },
    ],
  },
  {
    key: 'content',
    label: 'Contenu & Page d’accueil',
    icon: 'ri-home-8-line',
    permissions: [
      { key: 'content.read', label: 'Consulter le contenu de la plateforme' },
      { key: 'content.write', label: "Modifier la page d'accueil" },
    ],
  },
  {
    key: 'translations',
    label: 'Langues & Traductions',
    icon: 'ri-translate',
    permissions: [{ key: 'translations.manage', label: 'Gérer les langues et traductions' }],
  },
  {
    key: 'support',
    label: 'Support client',
    icon: 'ri-customer-service-2-line',
    permissions: [
      { key: 'support.read', label: 'Consulter les demandes de support' },
      { key: 'support.write', label: 'Répondre aux demandes de support' },
    ],
  },
  {
    key: 'domains',
    label: 'Domaines personnalisés',
    icon: 'ri-global-line',
    permissions: [
      { key: 'domains.read', label: 'Consulter les domaines' },
      { key: 'domains.write', label: 'Gérer les domaines personnalisés' },
    ],
  },
  {
    key: 'accountTypes',
    label: 'Types de compte',
    icon: 'ri-price-tag-3-line',
    permissions: [
      { key: 'accountTypes.read', label: 'Consulter les types de compte' },
      { key: 'accountTypes.write', label: 'Gérer les types de compte' },
    ],
  },
  {
    key: 'database',
    label: 'Base de données & API',
    icon: 'ri-database-2-line',
    permissions: [
      { key: 'database.read', label: 'Consulter la base de données' },
      { key: 'database.write', label: 'Exporter / administrer la base de données' },
    ],
  },
  {
    key: 'storage',
    label: 'Stockage',
    icon: 'ri-hard-drive-3-line',
    permissions: [
      { key: 'storage.read', label: 'Consulter le stockage' },
      { key: 'storage.write', label: 'Gérer le stockage' },
    ],
  },
  {
    key: 'logs',
    label: 'Logs système',
    icon: 'ri-terminal-line',
    permissions: [{ key: 'logs.read', label: 'Consulter les logs système' }],
  },
  {
    key: 'rates',
    label: 'Taux de change',
    icon: 'ri-exchange-dollar-line',
    permissions: [{ key: 'rates.manage', label: 'Gérer les taux de change' }],
  },
  {
    key: 'ai',
    label: 'Usage IA & Coûts',
    icon: 'ri-coin-line',
    permissions: [{ key: 'ai.read', label: "Consulter l'usage IA et les coûts" }],
  },
  {
    key: 'settings',
    label: 'Paramètres plateforme',
    icon: 'ri-shield-keyhole-line',
    permissions: [
      { key: 'settings.read', label: 'Consulter les paramètres' },
      { key: 'settings.write', label: 'Modifier les paramètres & clés API IA' },
    ],
  },
  {
    key: 'pwa',
    label: 'Application PWA',
    icon: 'ri-smartphone-line',
    permissions: [
      { key: 'pwa.read', label: 'Consulter les réglages PWA' },
      { key: 'pwa.manage', label: "Modifier l'application PWA" },
    ],
  },
  {
    key: 'core',
    label: 'Zifek Core',
    icon: 'ri-organization-chart',
    permissions: [
      { key: 'core.read', label: 'Consulter le socle Core' },
      { key: 'core.write', label: 'Administrer le socle Core' },
    ],
  },
];

export const ALL_PLATFORM_PERMISSIONS: string[] = PLATFORM_PERMISSION_GROUPS.flatMap((g) =>
  g.permissions.map((p) => p.key),
);

export function platformPermissionLabel(key: string): string {
  if (key === '*') return 'Accès complet';
  for (const g of PLATFORM_PERMISSION_GROUPS) {
    const found = g.permissions.find((p) => p.key === key);
    if (found) return found.label;
  }
  return key;
}

// ─────────────────────────────────────────────────────────────
// Règles d'accès aux routes du SuperAdmin.
// Les préfixes les plus longs gagnent. Pas de règle = accès autorisé.
// ─────────────────────────────────────────────────────────────

export interface PlatformRouteRule {
  prefix: string;
  permission: string | string[];
}

export const SUPERADMIN_ROUTE_PERMISSIONS: PlatformRouteRule[] = [
  { prefix: '/superadmin/users', permission: 'users.read' },
  { prefix: '/superadmin/team', permission: 'team.read' },
  { prefix: '/superadmin/activity', permission: 'activity.read' },
  { prefix: '/superadmin/themes', permission: 'themes.read' },
  { prefix: '/superadmin/apps', permission: 'apps.read' },
  { prefix: '/superadmin/formations', permission: 'formations.read' },
  { prefix: '/superadmin/homepage', permission: 'content.read' },
  { prefix: '/superadmin/translations', permission: 'translations.manage' },
  { prefix: '/superadmin/support', permission: 'support.read' },
  { prefix: '/superadmin/domains', permission: 'domains.read' },
  { prefix: '/superadmin/account-types', permission: 'accountTypes.read' },
  { prefix: '/superadmin/database', permission: 'database.read' },
  { prefix: '/superadmin/storage', permission: 'storage.read' },
  { prefix: '/superadmin/logs', permission: 'logs.read' },
  { prefix: '/superadmin/rates', permission: 'rates.manage' },
  { prefix: '/superadmin/ai-usage', permission: 'ai.read' },
  { prefix: '/superadmin/settings', permission: 'settings.read' },
  { prefix: '/superadmin/pwa', permission: 'pwa.read' },
  { prefix: '/superadmin/core', permission: 'core.read' },
];

export function requiredPlatformPermissionForPath(pathname: string): string | string[] | null {
  const path = (pathname.split('?')[0] || '/').replace(/\/+$/, '') || '/';
  let best: PlatformRouteRule | null = null;
  for (const rule of SUPERADMIN_ROUTE_PERMISSIONS) {
    if (path === rule.prefix || path.startsWith(`${rule.prefix}/`)) {
      if (!best || rule.prefix.length > best.prefix.length) best = rule;
    }
  }
  return best ? best.permission : null;
}